import { useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { download, setAutoDownloadFlag, sendDownloadReminderEmail } from '../../../utility/api';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import { isMobileDevice, isNonWindowsOS } from '../../../utility/utility';
import { SignedIn, SignedOut, SignInButton, useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import config from '../../../config.json';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * HostLanding Component
 * Hero section extracted from LandingPage, reformatted to match Aida component structure:
 * - Transparent background (inherits from page background zones)
 * - Desktop: absolute-positioned 1024px canvas (hidden lg:block)
 * - Mobile: vertically stacked flow layout (flex lg:hidden)
 * - Separate refs for desktop/mobile InView detection
 * - No isMobileDevice() — visibility is purely CSS/Tailwind
 */

const YELLOW = 'rgba(251, 191, 36, 1)';
const WHITE = 'rgba(255, 255, 255, 1)';

export const HostLanding = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const { user } = useUser();

  const handleJoinNetworkClick = useCallback(() => {
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Network', buttonLocation: 'HostLanding' });
    setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS());
  }, []);

  const handleDownloadClick = useCallback(async () => {
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Start Earning Rewards', buttonLocation: 'HostLanding' });
    if (isMobileDevice() || isNonWindowsOS()) {
      const email = user?.primaryEmailAddress?.emailAddress;
      if (email) {
        await sendDownloadReminderEmail(email);
        toast.success('Download reminder email sent!', {
          description: 'Check your email for a link to download Playcast on your PC.',
          duration: 5000,
          style: { background: '#fbbf24', color: '#000000', border: 'none' },
        });
      } else {
        toast.error('Unable to send reminder', {
          description: 'Email address not found.',
          style: { background: '#fbbf24', color: '#000000', border: 'none' },
        });
      }
    } else {
      download();
    }
  }, [user]);

  return (
    <>
      {/* ── Desktop Layout ── */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '444px',
          backgroundColor: 'transparent',
          overflow: 'visible',
          position: 'relative',
          marginBottom: '40px',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Main hero image */}
          <div
            style={{
              position: 'absolute',
              left: '469px',
              top: '89px',
              width: '471px',
              height: '302px',
              zIndex: 382,
              overflow: 'hidden',
              filter: 'drop-shadow(rgba(0, 0, 0, 0.52) 21px 12px 12px)',
            }}
          >
            <img
              src="/home-site/hero-main.png"
              srcSet="/home-site/hero-main.png 2x, /home-site/hero-main.png 3x"
              alt="Hero"
              className="fixMobileImageStretch"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 1 }}
              draggable={false}
            />
          </div>

          {/* Aurora cycling headline */}
          <div
            style={{ position: 'absolute', left: '84px', top: '139px', width: '480px', height: '84px', zIndex: 430 }}
          >
            <div className="rm-widget-aurora">
              <div className="rm-line">
                WHEN YOU'RE{' '}
                <span className="swap-stack">
                  <span className="word w1">SCHOOLING,</span>
                  <span className="word w2">WORKING,</span>
                  <span className="word w3">SLEEPING,</span>
                  <span className="word w4">TRAVELING,</span>
                  <span className="ghost">SCHOOLING,</span>
                </span>
              </div>
              <div className="rm-line">YOU EARN REWARDS</div>
            </div>
          </div>

          {/* Subtitle */}
          <div
            style={{ position: 'absolute', left: '88px', top: '228px', width: '353px', height: '13px', zIndex: 429 }}
          >
            <p className="text-style-paragraph-white" style={{ margin: 0, textAlign: 'left' }}>
              While your PC is idle, your computer works for you, earning real rewards.
            </p>
          </div>

          {/* CTA Button 1: Download / Join */}
          <div
            style={{ position: 'absolute', left: '84px', top: '271px', width: '158px', height: '30px', zIndex: 378 }}
          >
            <SignedIn>
              <button
                className="aida-button"
                onClick={handleDownloadClick}
                style={{ width: '100%', height: '30px', borderRadius: '30px', fontSize: '10px', gap: '6px' }}
              >
                <img
                  src="/img/aida/icons/RasterizedImage-e2051d0d-967b-4d95-bfd3-7d92d6ef1479.png"
                  alt=""
                  style={{ width: '8px', height: '8px', objectFit: 'contain', flexShrink: 0 }}
                  draggable={false}
                />
                <span className="text" style={{ height: '30px', lineHeight: '30px', padding: '0 4px' }}>
                  Start Earning Rewards
                </span>
              </button>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <button
                  className="aida-button"
                  onClick={handleJoinNetworkClick}
                  style={{ width: '100%', height: '30px', borderRadius: '30px', fontSize: '10px', gap: '6px' }}
                >
                  <img
                    src="/img/aida/icons/RasterizedImage-e2051d0d-967b-4d95-bfd3-7d92d6ef1479.png"
                    alt=""
                    style={{ width: '8px', height: '8px', objectFit: 'contain', flexShrink: 0 }}
                    draggable={false}
                  />
                  <span className="text" style={{ height: '30px', lineHeight: '30px', padding: '0 4px' }}>
                    Start Earning Rewards
                  </span>
                </button>
              </SignInButton>
            </SignedOut>
          </div>

          {/* CTA Button 2: Discord */}
          <div
            style={{ position: 'absolute', left: '253px', top: '270px', width: '157px', height: '31px', zIndex: 375 }}
          >
            <a
              href={config.DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'block', width: '100%', height: '100%' }}
              onClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Discord', buttonLocation: 'HostLanding' })}
            >
              <button
                className="aida-button aida-button-outlined"
                style={{ width: '100%', height: '31px', borderRadius: '30px', fontSize: '10px' }}
              >
                <span className="text" style={{ height: '31px', lineHeight: '31px' }}>
                  Join the Discord
                </span>
              </button>
            </a>
          </div>

          {/* Decorative images */}
          <motion.div
            style={{ position: 'absolute', left: '721px', top: '74px', width: '52px', height: '87px', zIndex: 388 }}
            animate={{ rotate: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-01.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '601px', top: '81px', width: '96px', height: '323px', zIndex: 384 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-02.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '765px', top: '135px', width: '174px', height: '87px', zIndex: 385 }}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-03.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '766px', top: '168px', width: '129px', height: '236px', zIndex: 389 }}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-04.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '895px', top: '168px', width: '45px', height: '77px', zIndex: 391 }}
            animate={{ x: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-05.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '469px', top: '211px', width: '112px', height: '67px', zIndex: 387 }}
            animate={{ rotate: [-5, 5, -5] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-06.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '913px', top: '269px', width: '26px', height: '79px', zIndex: 390 }}
            animate={{ rotate: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-07.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          <motion.div
            style={{ position: 'absolute', left: '469px', top: '270px', width: '112px', height: '134px', zIndex: 383 }}
            animate={{ x: [0, -13, 0], y: [0, 8, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src="/home-site/hero-decorative-08.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* ── Mobile Layout — stacked flow ── */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '60px 20px 40px',
          gap: '16px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Title */}
        <motion.div
          style={{ textAlign: 'center' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <p style={{ lineHeight: '39px', fontFamily: 'Inter', fontWeight: 900, fontSize: '27px', textAlign: 'center', margin: 0, color: WHITE, fontVariationSettings: "'wght' 900" }}>
            WHEN YOU'RE IDLE,
          </p>
          <p style={{ lineHeight: '39px', fontFamily: 'Inter', fontWeight: 900, fontSize: '27px', textAlign: 'center', margin: 0, fontVariationSettings: "'wght' 900" }}>
            <span style={{ color: WHITE }}>YOU EARN </span>
            <span style={{ color: YELLOW }}>REWARDS</span>
          </p>
        </motion.div>

        {/* Subtitle — under header */}
        <p className="text-style-paragraph-white" style={{ textAlign: 'center', maxWidth: '260px', margin: 0, fontSize: '16px', lineHeight: '22px' }}>
          While your PC is idle, your computer works for you, earning real rewards.
        </p>

        {/* Hero image + decoratives — all positioned relative to the same 304px canvas */}
        <div style={{ position: 'relative', width: '304px', height: '220px', overflow: 'visible', marginTop: '10px' }}>
          {/* Main hero image */}
          <motion.img
            src="/home-site/hero-main-mobile.png"
            srcSet="/home-site/hero-main-mobile.png 2x, /home-site/hero-main-mobile.png 3x"
            alt="Hero"
            className="fixMobileImageStretch"
            style={{ position: 'absolute', left: '0px', top: '10px', width: '304px', height: 'auto', filter: 'drop-shadow(rgba(0, 0, 0, 0.52) 21px 12px 12px)', zIndex: 2 }}
            draggable={false}
            initial={{ opacity: 0, y: 20 }}
            animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
          />
          {/* Decor 01 — rock */}
          <motion.div style={{ position: 'absolute', left: '163px', top: '0px', width: '34px', height: '56px', zIndex: 3 }} animate={{ rotate: [0, 10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-01.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 02 — float up */}
          <motion.div style={{ position: 'absolute', left: '85px', top: '5px', width: '62px', height: '208px', zIndex: 1 }} animate={{ y: [0, -10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-02.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 03 — float down */}
          <motion.div style={{ position: 'absolute', left: '191px', top: '40px', width: '112px', height: '56px', zIndex: 3 }} animate={{ y: [0, 10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-03.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 04 — float down */}
          <motion.div style={{ position: 'absolute', left: '191px', top: '61px', width: '83px', height: '152px', zIndex: 3 }} animate={{ y: [0, 10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-04.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 05 — float right */}
          <motion.div style={{ position: 'absolute', left: '270px', top: '61px', width: '29px', height: '50px', zIndex: 3 }} animate={{ x: [0, 20, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-05.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 06 — rock */}
          <motion.div style={{ position: 'absolute', left: '0px', top: '89px', width: '72px', height: '43px', zIndex: 3 }} animate={{ rotate: [-5, 5, -5] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-06.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 07 — rock */}
          <motion.div style={{ position: 'absolute', left: '277px', top: '126px', width: '17px', height: '51px', zIndex: 3 }} animate={{ rotate: [0, 10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-07.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
          {/* Decor 08 — diagonal drift */}
          <motion.div style={{ position: 'absolute', left: '0px', top: '127px', width: '72px', height: '86px', zIndex: 3 }} animate={{ x: [0, -13, 0], y: [0, 8, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
            <img src="/home-site/hero-decorative-08.png" alt="" className="fixMobileImageStretch" style={{ opacity: 1 }} draggable={false} />
          </motion.div>
        </div>

        {/* Buttons */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', marginTop: '24px' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
        >
          <SignedIn>
            <button
              className="aida-button aida-button-lg"
              onClick={handleDownloadClick}
              style={{ width: '207px', height: '35px', borderRadius: '44px', fontSize: '18px', lineHeight: '35px' }}
            >
              <span className="text" style={{ height: '35px', lineHeight: '35px' }}>Join the Network</span>
            </button>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button
                className="aida-button aida-button-lg"
                onClick={handleJoinNetworkClick}
                style={{ width: '207px', height: '35px', borderRadius: '44px', fontSize: '18px', lineHeight: '35px' }}
              >
                <span className="text" style={{ height: '35px', lineHeight: '35px' }}>Join the Network</span>
              </button>
            </SignInButton>
          </SignedOut>
          <a href={config.DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Discord', buttonLocation: 'HostLanding' })}>
            <button
              className="aida-button aida-button-outlined aida-button-lg"
              style={{ width: '207px', height: '35px', borderRadius: '44px', fontSize: '18px', lineHeight: '35px' }}
            >
              <span className="text" style={{ height: '35px', lineHeight: '35px' }}>Join the Discord</span>
            </button>
          </a>
        </motion.div>
      </motion.section>
    </>
  );
};
